import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowDecision, FlowNote, FlowParallel } from '../FlowPrimitives';

// ============================================================================
// Experiment 10 - Chemical Equilibrium: Qualitative and Quantitative Studies
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp10',
  title: 'Experiment 10: Chemical Equilibrium',
  objectives: [
    'Observe Le Chatelier\'s Principle through qualitative equilibrium shifts',
    'Investigate how adding reagents or diluting affects equilibrium position',
    'Determine Ka of a weak acid (acetic acid) from pH measurements',
    'Apply equilibrium concepts quantitatively using the 5% approximation rule',
  ],
  safetyNotes: [
    'FeCl₃ stains skin and clothing — wear gloves and lab coat',
    'NaOH is caustic — avoid skin contact, flush immediately if spilled',
    'HCl and acetic acid are corrosive — work in fume hood when handling concentrated solutions',
    'Wear safety goggles at all times',
  ],
  equipment: [
    'Test tubes (at least 10) and test tube rack',
    'pH meter with combination electrode',
    'pH buffer solutions (4.00, 7.00, 10.00)',
    'Beakers (100 mL, 250 mL)',
    'Graduated cylinders',
    'Volumetric flasks for dilution series',
    'Wash bottle with distilled water',
    'Dropper pipettes',
  ],
  chemicals: [
    'I₂/KI solution (brown, aqueous iodine)',
    'NaOH solution',
    'HCl solution (dilute)',
    'FeCl₃ solution',
    'KSCN solution',
    'FeSCN\u00B2⁺ equilibrium mixture',
    'Saturated NaCl solution',
    'Concentrated HCl',
    'Acetic acid (1.0 M stock)',
    'Distilled water',
  ],
  procedure: [
    {
      number: 1,
      title: 'System 1 — Iodine Equilibrium',
      details: [
        'Prepare 4 test tubes with I₂/KI solution (brown color)',
        'Tube 1: Reference — no addition (keep for comparison)',
        'Tube 2: Add NaOH — observe fading (equilibrium shifts right)',
        'Tube 3: Add HCl — observe darkening (equilibrium shifts left)',
        'Record all color changes and shift directions',
      ],
      manualRef: 'Part I: System 1',
    },
    {
      number: 2,
      title: 'System 2 — Iron Thiocyanate Equilibrium',
      details: [
        'Prepare 5 test tubes with FeSCN\u00B2⁺ equilibrium mixture',
        'Tube 1: Reference (blood-red color)',
        'Tube 2: Add FeCl₃ — darker red (shifts right)',
        'Tube 3: Add KSCN — darker red (shifts right)',
        'Tube 4: Add NaOH — lighter color (Fe\u00B3⁺ precipitates, shifts left)',
        'Tube 5: Dilute with water — lighter color (shifts left)',
      ],
      safetyNote: 'FeCl₃ stains; use gloves when handling',
      manualRef: 'Part I: System 2',
    },
    {
      number: 3,
      title: 'System 3 — Common Ion Effect',
      details: [
        'Prepare a saturated NaCl solution in a test tube',
        'Add concentrated HCl dropwise',
        'Observe NaCl precipitation due to the common ion effect (Cl⁻)',
      ],
      safetyNote: 'Use fume hood when handling concentrated HCl',
      manualRef: 'Part I: System 3',
    },
    {
      number: 4,
      title: 'Calibrate pH Meter',
      details: [
        'Immerse electrode in pH 7.00 buffer — set zero point',
        'Rinse with distilled water, immerse in pH 4.00 buffer — set slope',
        'Rinse and verify with pH 10.00 buffer',
        'Record actual reading for pH 10 buffer',
      ],
      manualRef: 'Part II: Calibration',
    },
    {
      number: 5,
      title: 'Prepare Acetic Acid Dilution Series',
      details: [
        'Prepare solutions: 1.0 M, 0.5 M, 0.2 M, 0.1 M acetic acid',
        'Use volumetric dilution from 1.0 M stock',
        'Label each flask clearly',
      ],
      manualRef: 'Part II: Preparation',
    },
    {
      number: 6,
      title: 'Measure pH and Calculate Ka',
      details: [
        'Measure pH of each acetic acid solution with calibrated pH meter',
        'Rinse electrode between measurements',
        'Calculate [H⁺] = 10^(−pH) for each solution',
        'Calculate Ka = [H⁺]\u00B2 / (C − [H⁺]) for each concentration',
        'Check 5% rule: if [H⁺] < 5% of C, the approximation is valid',
        'Calculate mean Ka and compare to literature (1.76 × 10⁻⁵)',
      ],
      manualRef: 'Part II: Measurement & Calculation',
    },
  ],
  keyFormulas: [
    'Ka = [H⁺]\u00B2 / (C − [H⁺])  where C = initial acid concentration',
    '[H⁺] = 10^(−pH)',
    'pKa = −log(Ka)',
    '5% Rule: [H⁺] / C < 0.05 validates approximation [HA] \u2248 C',
    'Fe\u00B3⁺ + SCN⁻ \u21CC FeSCN\u00B2⁺   (Keq = 890)',
  ],
  tips: [
    'Always compare test tubes to the reference tube side-by-side against a white background',
    'Rinse the pH electrode thoroughly with distilled water between each measurement',
    'Allow pH reading to stabilize for at least 30 seconds before recording',
    'Prepare dilutions carefully — small errors propagate into Ka calculation',
    'For the common ion effect, add HCl dropwise and swirl gently to observe gradual precipitation',
  ],
};

// ============================================================================
// Flowchart
// ============================================================================

function Flowchart() {
  // Layout: center at x=210 for 600px container, nodes 180px wide
  // Vertical spacing ~80px between nodes
  const cx = 210; // center x for main nodes (width 180)
  const nw = 180; // node width
  const nh = 56;  // node height

  return (
    <FlowContainer width={600} height={820} title="Experiment 10: Procedure Flow">
      {/* Start terminal */}
      <FlowTerminal x={230} y={44} width={140} height={36} label="Start" color="#6366f1" />

      {/* Arrow: Start -> System 1 */}
      <FlowArrow from={{ x: 300, y: 80 }} to={{ x: 300, y: 100 }} />

      {/* Step 1: Iodine Equilibrium */}
      <FlowNode x={cx} y={100} width={nw} height={nh} title="System 1: I₂ Equilibrium" subtitle="4 tubes: ref, NaOH, HCl" color="#0d9488" />

      {/* Arrow: System 1 -> System 2 */}
      <FlowArrow from={{ x: 300, y: 156 }} to={{ x: 300, y: 180 }} label="observe shifts" />

      {/* Step 2: Iron Thiocyanate */}
      <FlowNode x={cx} y={180} width={nw} height={nh} title="System 2: FeSCN\u00B2⁺" subtitle="5 tubes: Fe\u00B3⁺, SCN⁻, NaOH, H₂O" color="#0d9488" />

      {/* Arrow: System 2 -> System 3 */}
      <FlowArrow from={{ x: 300, y: 236 }} to={{ x: 300, y: 260 }} label="observe shifts" />

      {/* Step 3: Common Ion */}
      <FlowNode x={cx} y={260} width={nw} height={nh} title="System 3: Common Ion" subtitle="Sat. NaCl + conc. HCl" color="#0d9488" />

      {/* Note for Part I */}
      <FlowNote x={420} y={186} text="Part I: Qualitative" width={130} color="#f59e0b" />

      {/* Arrow: System 3 -> Calibrate pH */}
      <FlowArrow from={{ x: 300, y: 316 }} to={{ x: 300, y: 350 }} label="proceed to Part II" />

      {/* Step 4: Calibrate pH meter */}
      <FlowNode x={cx} y={350} width={nw} height={nh} title="Calibrate pH Meter" subtitle="pH 7.00, 4.00, 10.00 buffers" color="#6366f1" />

      {/* Arrow: Calibrate -> Prepare dilutions */}
      <FlowArrow from={{ x: 300, y: 406 }} to={{ x: 300, y: 430 }} />

      {/* Step 5: Prepare dilutions */}
      <FlowNode x={cx} y={430} width={nw} height={nh} title="Prepare Dilution Series" subtitle="1.0, 0.5, 0.2, 0.1 M" color="#6366f1" />

      {/* Arrow: Prepare -> Measure pH */}
      <FlowArrow from={{ x: 300, y: 486 }} to={{ x: 300, y: 510 }} />

      {/* Step 6: Measure pH */}
      <FlowNode x={cx} y={510} width={nw} height={nh} title="Measure pH" subtitle="Each concentration" color="#6366f1" />

      {/* Note for Part II */}
      <FlowNote x={420} y={436} text="Part II: Quantitative" width={140} color="#6366f1" />

      {/* Arrow: Measure -> Decision */}
      <FlowArrow from={{ x: 300, y: 566 }} to={{ x: 300, y: 600 }} />

      {/* Decision: 5% rule */}
      <FlowDecision x={265} y={600} size={70} label="5% rule?" color="#f59e0b" />

      {/* Arrow: Decision yes -> Calculate Ka */}
      <FlowArrow from={{ x: 300, y: 670 }} to={{ x: 300, y: 700 }} label="valid" />

      {/* Arrow: Decision no -> note */}
      <FlowArrow from={{ x: 335, y: 635 }} to={{ x: 420, y: 635 }} label="invalid" dashed />
      <FlowNote x={420} y={620} text="Use quadratic formula" width={140} color="#ef4444" />

      {/* Step 7: Calculate Ka */}
      <FlowNode x={cx} y={700} width={nw} height={nh} title="Calculate Ka" subtitle="Ka = [H⁺]\u00B2 / (C − [H⁺])" color="#0d9488" />

      {/* Arrow: Calculate -> End */}
      <FlowArrow from={{ x: 300, y: 756 }} to={{ x: 300, y: 776 }} />

      {/* End terminal */}
      <FlowTerminal x={230} y={776} width={140} height={36} label="Report Results" color="#6366f1" />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp10Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
